import React from 'react';
import { SettingCard, SettingCardProps } from '../SettingCard/SettingCard';
import { HelpIcon } from '../../../Icons/HelpIcon/HelpIcon';
import './RadioSetting.css';

export interface RadioSettingOption {
	value: string;
	label: string;
	description?: string;
	content?: React.ReactNode;
}

export interface IconRadioOption extends RadioSettingOption {
	icon: React.ComponentType<{ className?: string }>;
}

export interface RadioSettingProps extends Omit<
	SettingCardProps,
	'content' | 'className' | 'showContentSeparator'
> {
	options: RadioSettingOption[] | IconRadioOption[];
	selectedValue: string;
	onChange: (value: string) => void;
	card?: boolean;
	deprecated?: boolean;
}

export const RadioSetting: React.FC<RadioSettingProps> = ({
	title,
	description,
	options,
	selectedValue,
	onChange,
	settingId,
	disabled = false,
	showHelpIcon = false,
	helpText = '',
	showStatusOnValueChange = false,
	statusMessage,
	card = true,
	learnMoreUrl,
	learnMoreText,
	deprecated = false,
}) => {
	const handleChange = (value: string) => {
		if (!disabled) {
			onChange(value);
		}
	};

	// Create custom title with help icon if needed
	const renderTitle = () => {
		if (typeof title === 'string') {
			return (
				<div className="radio-setting-title-container">
					<h3>{title}</h3>
					{showHelpIcon && helpText && (
						<HelpIcon helpText={helpText} className="setting-card-help-icon" />
					)}
				</div>
			);
		}
		return title;
	};

	// Type guard to check if options are IconRadioOption[]
	const isIconRadioOptions = (
		opts: RadioSettingOption[] | IconRadioOption[]
	): opts is IconRadioOption[] => {
		return opts.length > 0 && 'icon' in opts[0];
	};

	const isIconOptions = isIconRadioOptions(options);

	const renderTextOptions = () => (
		<div className="standard-radio-group">
			{(options as RadioSettingOption[]).map((option) => {
				return (
					<div key={option.value} className="standard-radio-option">
						<label className="standard-radio-option-label">
							<input
								type="radio"
								name={settingId}
								value={option.value}
								checked={selectedValue === option.value}
								onChange={() => handleChange(option.value)}
								className="standard-radio-input"
								disabled={disabled}
							/>
							<span className="standard-radio-button"></span>
							<div className="standard-radio-label">{option.label}</div>
						</label>
						{option.description && (
							<div className="standard-radio-description">{option.description}</div>
						)}
						{option.content && (
							<div className="standard-radio-custom-content">{option.content}</div>
						)}
					</div>
				);
			})}
		</div>
	);

	const renderIconOptions = () => (
		<>
			<div className="icon-option-radio-group">
				{(options as IconRadioOption[]).map((option) => {
					const isSelected = selectedValue === option.value;

					return (
						<div
							key={option.value}
							className={`icon-option-radio-option ${isSelected ? 'selected' : ''} ${
								disabled ? 'disabled' : ''
							}`}
						>
							<label className="icon-option-radio-option-label">
								<input
									type="radio"
									name={settingId}
									value={option.value}
									checked={isSelected}
									onChange={() => handleChange(option.value)}
									className="icon-option-radio-input"
									disabled={disabled}
								/>
								<div className="icon-option-radio-content-horizontal">
									<div className="icon-option-radio-icon-container">
										<option.icon />
										<div className="icon-option-radio-text-center">
											<div className="icon-option-radio-label">{option.label}</div>
										</div>
										<div className="icon-option-radio-indicator">
											<div className="circular-checkbox"></div>
										</div>
									</div>
								</div>
							</label>
						</div>
					);
				})}
			</div>
			{(options as IconRadioOption[]).find((opt) => opt.value === selectedValue)
				?.description && (
				<div className="icon-option-radio-description-selected">
					{
						(options as IconRadioOption[]).find((opt) => opt.value === selectedValue)
							?.description
					}
				</div>
			)}
		</>
	);

	if (!card) {
		return (
			<>
				{renderTitle()}
				{description && <div className="radio-setting-description">{description}</div>}
				{isIconOptions ? renderIconOptions() : renderTextOptions()}
			</>
		);
	}
	return (
		<SettingCard
			title={renderTitle()}
			description={description}
			settingId={settingId}
			showStatusOnValueChange={showStatusOnValueChange}
			statusMessage={statusMessage}
			disabled={disabled}
			content={isIconOptions ? renderIconOptions() : renderTextOptions()}
			learnMoreUrl={learnMoreUrl}
			learnMoreText={learnMoreText}
			deprecated={deprecated}
		/>
	);
};
